import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles, fade } from '@material-ui/core/styles';
import {
	Typography,
	IconButton,
	Button,
	Tooltip,
	TextField,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Grow,
	Fade
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import SearchIcon from '@material-ui/icons/Search';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import clsx from 'clsx';
import JString from '@easytech-international-sdn-bhd/jstring';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../Services/API_Service';
import { req_promotion_type, req_promotion_type_delete } from '../../Helper/Constants';
import LinearLoading from '../../Components/LinearLoading';
import PromotionTypeDialog from '../../Components/PromotionTypeDialog';

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
	return <Grow ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: theme.spacing(0.5)
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5)
	},
	titleWrap: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		marginRight: 'auto'
	},
	title: {
		fontWeight: 700,
		letterSpacing: '-0.01em'
	},
	countBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 26,
		height: 22,
		padding: theme.spacing(0, 0.75),
		borderRadius: 11,
		fontSize: 12,
		fontWeight: 700,
		fontVariantNumeric: 'tabular-nums',
		color: theme.palette.text.secondary,
		backgroundColor: fade(theme.palette.text.primary, 0.06)
	},
	search: {
		width: 240,
		maxWidth: '40vw',
		[theme.breakpoints.down('xs')]: {
			width: '100%',
			maxWidth: '100%'
		},
		'& .MuiOutlinedInput-root': {
			borderRadius: 10,
			backgroundColor: theme.palette.background.paper,
			transition: theme.transitions.create(['box-shadow', 'border-color'], { duration: 180 })
		},
		'& .MuiOutlinedInput-root.Mui-focused': {
			boxShadow: `0 0 0 3px ${fade('#FEF200', 0.35)}`
		}
	},
	searchIcon: {
		color: theme.palette.text.disabled,
		fontSize: 20
	},
	newBtn: {
		backgroundColor: '#FEF200',
		color: 'rgba(0,0,0,0.86)',
		fontWeight: 700,
		borderRadius: 10,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		boxShadow: `0 1px 2px ${fade('#000', 0.2)}`,
		transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], { duration: 180 }),
		'&:hover': {
			backgroundColor: '#FFF53D',
			transform: 'translateY(-1px)',
			boxShadow: `0 6px 16px ${fade('#FEF200', 0.5)}`
		},
		'&:active': {
			transform: 'translateY(0)'
		}
	},
	tableWrap: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 12,
		overflow: 'hidden',
		backgroundColor: theme.palette.background.paper
	},
	tableScroll: {
		overflowX: 'auto'
	},
	table: {
		minWidth: 640
	},
	headRow: {
		'& th': {
			borderBottom: `1px solid ${theme.palette.divider}`,
			backgroundColor: theme.palette.type === 'dark'
				? fade(theme.palette.common.white, 0.03)
				: fade(theme.palette.text.primary, 0.02)
		}
	},
	headCell: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.06em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		padding: theme.spacing(0.9, 2)
	},
	numHead: {
		textAlign: 'right'
	},
	actionHead: {
		textAlign: 'right',
		width: 108
	},
	'@keyframes rowIn': {
		from: { opacity: 0, transform: 'translateY(7px)' },
		to: { opacity: 1, transform: 'none' }
	},
	row: {
		animation: '$rowIn 280ms cubic-bezier(0.16, 1, 0.3, 1) both',
		transition: theme.transitions.create('background-color', { duration: 150 }),
		'& td': {
			padding: theme.spacing(1, 2),
			borderBottom: `1px solid ${fade(theme.palette.divider, 0.6)}`
		},
		'&:last-child td': {
			borderBottom: 'none'
		},
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		},
		'&:hover $actions': {
			opacity: 1
		}
	},
	nameCell: {
		fontWeight: 600,
		color: theme.palette.text.primary
	},
	descCell: {
		color: theme.palette.text.secondary,
		maxWidth: 320,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	muted: {
		color: theme.palette.text.disabled
	},
	promoCell: {
		textAlign: 'right',
		fontVariantNumeric: 'tabular-nums',
		fontWeight: 600,
		color: theme.palette.text.primary
	},
	promoZero: {
		color: theme.palette.text.disabled,
		fontWeight: 400
	},
	statusPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing(0.75),
		height: 24,
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1.25),
		borderRadius: 12,
		fontSize: 12.5,
		fontWeight: 600
	},
	statusDot: {
		fontSize: 9
	},
	statusActive: {
		color: theme.palette.type === 'dark' ? '#7ee2a8' : '#1b7a43',
		backgroundColor: fade(theme.palette.success.main, theme.palette.type === 'dark' ? 0.18 : 0.14)
	},
	statusInactive: {
		color: theme.palette.text.secondary,
		backgroundColor: fade(theme.palette.text.primary, 0.07)
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: theme.spacing(0.5),
		opacity: 0.35,
		transition: theme.transitions.create('opacity', { duration: 150 })
	},
	iconBtn: {
		padding: 7,
		color: theme.palette.text.secondary,
		transition: theme.transitions.create(['color', 'background-color'], { duration: 150 }),
		'&:hover': {
			color: theme.palette.text.primary,
			backgroundColor: fade(theme.palette.text.primary, 0.08)
		}
	},
	deleteBtn: {
		'&:hover': {
			color: theme.palette.error.main,
			backgroundColor: fade(theme.palette.error.main, 0.12)
		}
	},
	empty: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: theme.spacing(5, 3, 5.5)
	},
	emptyIconWrap: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 54,
		height: 54,
		borderRadius: 16,
		marginBottom: theme.spacing(2),
		color: 'rgba(0,0,0,0.7)',
		backgroundColor: '#FEF200'
	},
	emptyIcon: {
		fontSize: 32
	},
	emptyTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5)
	},
	emptyHint: {
		color: theme.palette.text.secondary,
		maxWidth: 360,
		marginBottom: theme.spacing(3)
	},
	skelRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		padding: theme.spacing(1.1, 2),
		borderBottom: `1px solid ${fade(theme.palette.divider, 0.6)}`
	},
	pagination: {
		borderTop: `1px solid ${theme.palette.divider}`
	},
	dangerName: {
		fontWeight: 700,
		color: theme.palette.text.primary
	},
	deleteConfirmBtn: {
		color: '#fff',
		backgroundColor: theme.palette.error.main,
		fontWeight: 700,
		'&:hover': {
			backgroundColor: theme.palette.error.dark
		}
	},
	d0: { animationDelay: '0ms' },
	d1: { animationDelay: '35ms' },
	d2: { animationDelay: '70ms' },
	d3: { animationDelay: '105ms' },
	d4: { animationDelay: '140ms' },
	d5: { animationDelay: '175ms' },
	d6: { animationDelay: '210ms' },
	d7: { animationDelay: '245ms' }
}));

const ROWS_PER_PAGE = 20;

export default function PromotionType() {
	const classes = useStyles();
	const [gState] = useContext(AppContext);
	const { enqueueSnackbar } = useSnackbar();

	const singleton = PersistantData.getInstance();
	var { translation: language } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	const [list, setList] = useState([]);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [query, setQuery] = useState('');
	const [page, setPage] = useState(0);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState('add');
	const [chosen, setChosen] = useState(null);
	const [confirm, setConfirm] = useState({ open: false, id: null, name: '' });
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		if (JString(gState.client).isValid()) {
			loadList(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gState.client]);

	const loadList = (initial = false) => {
		if (initial) {
			setLoading(true);
		} else {
			setRefreshing(true);
		}
		API_Service.get(req_promotion_type, axiosDefaultConfig(gState.client))
			.then(({ data }) => {
				const { result, extraData } = data;
				if (parseInt(result) === 1) {
					setList(Array.isArray(extraData) ? extraData : []);
				} else {
					setList([]);
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }))
			.finally(() => {
				setLoading(false);
				setRefreshing(false);
			});
	};

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) {
			return list;
		}
		return list.filter((it) => {
			const name = (it.name || '').toLowerCase();
			const desc = (it.description || '').toLowerCase();
			return name.indexOf(q) > -1 || desc.indexOf(q) > -1;
		});
	}, [list, query]);

	const pageCount = Math.ceil(filtered.length / ROWS_PER_PAGE);
	const safePage = page >= pageCount ? 0 : page;
	const paged = filtered.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

	const onSearch = (e) => {
		setQuery(e.target.value);
		setPage(0);
	};

	const onAdd = () => {
		setDialogMode('add');
		setChosen(null);
		setDialogOpen(true);
	};

	const onEdit = (row) => {
		setDialogMode('edit');
		setChosen(row);
		setDialogOpen(true);
	};

	const onDialogClose = (didChange) => {
		setDialogOpen(false);
		if (didChange) {
			loadList(false);
		}
	};

	const onAskDelete = (row) => setConfirm({ open: true, id: row.id, name: row.name });

	const closeConfirm = () => {
		if (deleting) {
			return;
		}
		setConfirm({ open: false, id: null, name: '' });
	};

	const doDelete = () => {
		setDeleting(true);
		const params = axiosURIencode({ id: confirm.id });
		API_Service.put(req_promotion_type_delete, params, axiosPutConfig(gState.client))
			.then(({ data }) => {
				const { result, message } = data;
				if (parseInt(result) === 1) {
					enqueueSnackbar(message || language.update_success, { variant: 'success' });
					setConfirm({ open: false, id: null, name: '' });
					loadList(false);
				} else {
					enqueueSnackbar(message || language.update_fail, { variant: 'error' });
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet || 'Network error', { variant: 'error' }))
			.finally(() => setDeleting(false));
	};

	const renderStatus = (value) => {
		const active = parseInt(value) === 1;
		return (
			<span className={clsx(classes.statusPill, active ? classes.statusActive : classes.statusInactive)}>
				<FiberManualRecordIcon className={classes.statusDot} />
				{active ? (language.active || 'Active') : (language.inactive || 'Inactive')}
			</span>
		);
	};

	const renderSkeleton = () => (
		<div className={classes.tableWrap}>
			{[0, 1, 2, 3, 4, 5].map((i) => (
				<div key={i} className={classes.skelRow}>
					<Skeleton animation="wave" variant="text" width={`${28 - i * 2}%`} height={20} />
					<Skeleton animation="wave" variant="text" width="32%" height={16} />
					<Skeleton animation="wave" variant="rect" width={64} height={22} />
				</div>
			))}
		</div>
	);

	const renderEmpty = (searching) => (
		<div className={classes.tableWrap}>
			<div className={classes.empty}>
				<span className={classes.emptyIconWrap}>
					<LocalOfferOutlinedIcon className={classes.emptyIcon} />
				</span>
				<Typography variant="h6" className={classes.emptyTitle}>
					{searching ? (language.no_data || 'No data found') : (language.no_promotion_types || 'No promotion types yet')}
				</Typography>
				{!searching && (
					<Typography variant="body2" className={classes.emptyHint}>
						{language.promotion_types_empty_hint || 'Create your first type to categorize promotions.'}
					</Typography>
				)}
				{!searching && (
					<Button
						variant="contained"
						disableElevation
						className={classes.newBtn}
						startIcon={<AddRoundedIcon />}
						onClick={onAdd}>
						{language.new_promotion_type}
					</Button>
				)}
			</div>
		</div>
	);

	return (
		<div className={classes.root}>
			<div className={classes.toolbar}>
				<div className={classes.titleWrap}>
					<Typography variant="h5" className={classes.title}>
						{language.promotion_types}
					</Typography>
					{!loading && list.length > 0 && (
						<span className={classes.countBadge}>{filtered.length}</span>
					)}
				</div>
				{!loading && list.length > 0 && (
					<TextField
						className={classes.search}
						variant="outlined"
						size="small"
						placeholder={language.search}
						value={query}
						onChange={onSearch}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon className={classes.searchIcon} />
								</InputAdornment>
							)
						}}
					/>
				)}
				<Button
					variant="contained"
					disableElevation
					className={classes.newBtn}
					startIcon={<AddRoundedIcon />}
					onClick={onAdd}>
					{language.new_promotion_type}
				</Button>
			</div>

			{refreshing && <LinearLoading />}

			{loading ? (
				renderSkeleton()
			) : filtered.length === 0 ? (
				renderEmpty(list.length > 0)
			) : (
				<Fade in timeout={220}>
					<div className={classes.tableWrap}>
						<div className={classes.tableScroll}>
							<Table className={classes.table}>
								<TableHead>
									<TableRow className={classes.headRow}>
										<TableCell className={classes.headCell}>{language.promotion_type_name}</TableCell>
										<TableCell className={classes.headCell}>{language.description}</TableCell>
										<TableCell className={clsx(classes.headCell, classes.numHead)}>{language.active_promo}</TableCell>
										<TableCell className={classes.headCell}>{language.status}</TableCell>
										<TableCell className={clsx(classes.headCell, classes.actionHead)}>{language.action}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paged.map((row, idx) => (
										<TableRow key={row.id} className={clsx(classes.row, classes[`d${Math.min(idx, 7)}`])}>
											<TableCell className={classes.nameCell}>{row.name}</TableCell>
											<TableCell className={clsx(classes.descCell, !row.description && classes.muted)}>
												{row.description || '—'}
											</TableCell>
											<TableCell className={clsx(classes.promoCell, parseInt(row.active_promo) > 0 ? null : classes.promoZero)}>
												{parseInt(row.active_promo) > 0 ? row.active_promo : '–'}
											</TableCell>
											<TableCell>{renderStatus(row.status)}</TableCell>
											<TableCell>
												<div className={classes.actions}>
													<Tooltip title={language.edit || 'Edit'} arrow>
														<IconButton
															className={classes.iconBtn}
															onClick={(e) => { e.stopPropagation(); onEdit(row); }}>
															<EditOutlinedIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title={language.delete} arrow>
														<IconButton
															className={clsx(classes.iconBtn, classes.deleteBtn)}
															onClick={(e) => { e.stopPropagation(); onAskDelete(row); }}>
															<DeleteOutlineIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						{filtered.length > ROWS_PER_PAGE && (
							<TablePagination
								className={classes.pagination}
								component="div"
								count={filtered.length}
								page={safePage}
								onChangePage={(e, p) => setPage(p)}
								rowsPerPage={ROWS_PER_PAGE}
								rowsPerPageOptions={[ROWS_PER_PAGE]}
							/>
						)}
					</div>
				</Fade>
			)}

			<PromotionTypeDialog
				open={dialogOpen}
				mode={dialogMode}
				data={chosen}
				client={gState.client}
				language={language}
				onSnack={(msg, variant = 'success') => enqueueSnackbar(msg, { variant })}
				onClose={onDialogClose}
			/>

			<Dialog
				open={confirm.open}
				onClose={closeConfirm}
				TransitionComponent={GrowTransition}
				maxWidth="xs">
				<DialogTitle>{language.confirmation}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{language.promotion_type_delete}
					</DialogContentText>
					{confirm.name ? (
						<Typography variant="body2" className={classes.dangerName}>{confirm.name}</Typography>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeConfirm} color="inherit" disabled={deleting}>
						{language.cancel}
					</Button>
					<Button
						onClick={doDelete}
						className={classes.deleteConfirmBtn}
						variant="contained"
						disableElevation
						disabled={deleting}>
						{language.delete}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
